import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Divider,
  Alert,
  Tooltip,
  Chip,
  Stack,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Save as SaveIcon,
  Restore as RestoreIcon,
  AttachFile as AttachFileIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { emailQueueAPI, emailEventsAPI } from '../services/api';
import { extractErrorMessage } from '../utils/errorHandler';
import { EmailEvent } from '../types/emailEvent';

interface EmailComposeDialogProps {
  open: boolean;
  onClose: () => void;
  toEmail: string;
  toName?: string;
  defaultSubject?: string;
  defaultBody?: string;
  eventCode?: string; // Email event code for DB template persistence
  onSuccess?: () => void;
  title?: string;
}

// Default templates (fallback if no event found)
const DEFAULT_SUBJECT = 'Relance - Situation de compte client';
const DEFAULT_BODY = `Bonjour,

Nous nous permettons de vous contacter concernant votre situation de compte.

Merci de bien vouloir régulariser votre situation dans les meilleurs délais.

Pour toute question, n'hésitez pas à nous contacter.

Cordialement,
L'équipe MAF Agencement`;

// Max file size: 10MB
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.png', '.jpg', '.jpeg', '.gif', '.txt', '.csv'];

const EmailComposeDialog: React.FC<EmailComposeDialogProps> = ({
  open,
  onClose,
  toEmail,
  toName,
  defaultSubject,
  defaultBody,
  eventCode = 'COMPTE_CLIENT_RELANCE',
  onSuccess,
  title = 'Envoyer un Email',
}) => {
  const [email, setEmail] = useState(toEmail);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [templateModified, setTemplateModified] = useState(false);
  const [emailEvent, setEmailEvent] = useState<EmailEvent | null>(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [originalSubject, setOriginalSubject] = useState('');
  const [originalBody, setOriginalBody] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load template from database
  useEffect(() => {
    if (open && eventCode) {
      setLoadingTemplate(true);
      emailEventsAPI.getByCode(eventCode)
        .then((event) => {
          setEmailEvent(event);
          const subj = event.template_subject || defaultSubject || DEFAULT_SUBJECT;
          const bod = event.template_body || defaultBody || DEFAULT_BODY;
          setSubject(subj);
          setBody(bod);
          setOriginalSubject(subj);
          setOriginalBody(bod);
        })
        .catch(() => {
          // Fallback to defaults if event not found
          const subj = defaultSubject || DEFAULT_SUBJECT;
          const bod = defaultBody || DEFAULT_BODY;
          setSubject(subj);
          setBody(bod);
          setOriginalSubject(subj);
          setOriginalBody(bod);
        })
        .finally(() => {
          setLoadingTemplate(false);
        });

      setEmail(toEmail);
      setError(null);
      setSuccess(false);
      setTemplateModified(false);
      setAttachments([]);
    }
  }, [open, toEmail, defaultSubject, defaultBody, eventCode]);

  // Check if template is modified
  useEffect(() => {
    const subjectChanged = subject !== originalSubject;
    const bodyChanged = body !== originalBody;
    setTemplateModified(subjectChanged || bodyChanged);
  }, [subject, body, originalSubject, originalBody]);

  const handleSaveTemplate = async () => {
    if (!emailEvent) {
      setError('Impossible de sauvegarder: événement email non trouvé');
      return;
    }

    setSaving(true);
    try {
      await emailEventsAPI.updateTemplate(emailEvent.id, {
        template_subject: subject,
        template_body: body,
      });
      setOriginalSubject(subject);
      setOriginalBody(body);
      setTemplateModified(false);
    } catch (err: any) {
      setError(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreDefault = () => {
    setSubject(originalSubject);
    setBody(originalBody);
    setTemplateModified(false);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newFiles: File[] = [];
    const errors: string[] = [];

    Array.from(files).forEach((file) => {
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: Fichier trop volumineux (max 10MB)`);
        return;
      }

      // Check extension
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        errors.push(`${file.name}: Type de fichier non autorisé`);
        return;
      }

      // Check if already added
      if (attachments.some(a => a.name === file.name && a.size === file.size)) {
        errors.push(`${file.name}: Fichier déjà ajouté`);
        return;
      }

      newFiles.push(file);
    });

    if (errors.length > 0) {
      setError(errors.join('\n'));
    }

    if (newFiles.length > 0) {
      setAttachments(prev => [...prev, ...newFiles]);
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSend = async () => {
    if (!email.trim()) {
      setError('L\'adresse email est requise');
      return;
    }
    if (!subject.trim()) {
      setError('Le sujet est requis');
      return;
    }
    if (!body.trim()) {
      setError('Le corps du message est requis');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Adresse email invalide');
      return;
    }

    setSending(true);
    setError(null);

    try {
      // Convert plain text to HTML
      const htmlBody = `
        <html>
        <body style="font-family: Arial, sans-serif; padding: 20px; line-height: 1.6;">
          ${body.split('\n').map(line => line.trim() === '' ? '<br>' : `<p style="margin: 0 0 10px 0;">${line}</p>`).join('')}
        </body>
        </html>
      `;

      // Send directly with attachments
      await emailQueueAPI.sendDirect({
        to_email: email,
        to_name: toName,
        subject,
        body: htmlBody,
        attachments: attachments.length > 0 ? attachments : undefined,
      });

      setSuccess(true);

      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(extractErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (!sending && !saving) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '70vh',
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{
        bgcolor: '#4CAF50',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 1.5,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SendIcon />
          <Typography variant="h6">{title}</Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={sending || saving}
          sx={{ color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loadingTemplate ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Email Header Section */}
            <Box sx={{ p: 2, bgcolor: 'background.default', borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ width: 60, fontWeight: 500, color: 'text.secondary' }}>À:</Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@exemple.com"
                  disabled={sending}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                    }
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ width: 60, fontWeight: 500, color: 'text.secondary' }}>Objet:</Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Sujet de l'email"
                  disabled={sending}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                    }
                  }}
                />
              </Box>
            </Box>

            {/* Template Actions */}
            <Box sx={{
              px: 2,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: 'background.default',
              borderBottom: 1,
              borderColor: 'divider',
            }}>
              <Typography variant="caption" color="text.secondary">
                {templateModified ? '* Modèle modifié (non sauvegardé)' : 'Modèle sauvegardé'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Sauvegarder le modèle dans la base de données">
                  <span>
                    <Button
                      size="small"
                      startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                      onClick={handleSaveTemplate}
                      disabled={!templateModified || sending || saving || !emailEvent}
                      sx={{ textTransform: 'none' }}
                    >
                      Sauvegarder
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Restaurer le modèle enregistré">
                  <Button
                    size="small"
                    startIcon={<RestoreIcon />}
                    onClick={handleRestoreDefault}
                    disabled={!templateModified || sending || saving}
                    sx={{ textTransform: 'none' }}
                  >
                    Restaurer
                  </Button>
                </Tooltip>
              </Box>
            </Box>

            {/* Email Body */}
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                multiline
                rows={12}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Rédigez votre message ici..."
                disabled={sending}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'inherit',
                    fontSize: '14px',
                    lineHeight: 1.6,
                  }
                }}
              />
            </Box>

            {/* Attachments Section */}
            <Box sx={{ px: 2, pb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  multiple
                  style={{ display: 'none' }}
                  accept={ALLOWED_EXTENSIONS.join(',')}
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AttachFileIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending}
                  sx={{ textTransform: 'none' }}
                >
                  Joindre des fichiers
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Max 10MB par fichier
                </Typography>
              </Box>

              {attachments.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {attachments.map((file, index) => (
                    <Chip
                      key={`${file.name}-${index}`}
                      icon={<FileIcon />}
                      label={`${file.name} (${formatFileSize(file.size)})`}
                      onDelete={() => handleRemoveAttachment(index)}
                      disabled={sending}
                      sx={{ mb: 1 }}
                    />
                  ))}
                </Stack>
              )}
            </Box>

            {/* Status Messages */}
            {error && (
              <Box sx={{ px: 2, pb: 2 }}>
                <Alert severity="error" onClose={() => setError(null)}>
                  {error.split('\n').map((line, i) => (
                    <div key={i}>{line}</div>
                  ))}
                </Alert>
              </Box>
            )}
            {success && (
              <Box sx={{ px: 2, pb: 2 }}>
                <Alert severity="success">
                  Email envoyé avec succès !
                </Alert>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button
          onClick={handleClose}
          disabled={sending || saving}
          color="inherit"
        >
          Annuler
        </Button>
        <Button
          variant="contained"
          startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
          onClick={handleSend}
          disabled={sending || success || loadingTemplate}
          sx={{
            bgcolor: '#4CAF50',
            '&:hover': { bgcolor: '#388E3C' },
            minWidth: 150,
          }}
        >
          {sending ? 'Envoi...' : 'Envoyer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailComposeDialog;
